import React, { Component, RefObject } from 'react'
import { connect } from "react-redux"
import * as d3 from "d3"
import { find } from 'lodash';
import { SizeType } from 'products/components/filters';
import { selectFormValues, updateInputValue } from 'app/reducers/form';
import { setActiveShapeId } from 'shape-edit/reducers';
import { isJson } from 'app/helpers/other';
import { URL } from 'app/config/api-config';
import {  getFormName, getSubdividedPoints, randomIntFromInterval } from 'shape-edit/forms/helpers';
import { AlternativeTemplate, AnyPoint, BrushFormType, Project, Template } from 'app/types/model-types';
import BrushShape from "../types/BrushShape";
import { getBrushPatternCursor } from 'app/helpers/api';
import { AxiosResponse } from 'axios';
import { getLineWidth, getPathFromPoints, LINEWIDTH_PROFILE_UNIFORM } from 'shape-edit/forms/brush-form';
import { testUsers } from 'template-edit/components/helper';
import { TemplateOrAlternativeTemplateType } from 'template-edit/undoRedoReducers';

export type BrushStrokeType = BrushFormType & { points: AnyPoint[] }
export const BRUSH_MIN_SPACING = 5

interface Props {
    isAlt: boolean
    form: TemplateOrAlternativeTemplateType
    brushForm: BrushFormType
    data: BrushShape
    size: SizeType
    activeShapeId: string
    project: Project
    updateInputValue: typeof updateInputValue
    setActiveShapeId: typeof setActiveShapeId
}

interface State {
    brushStrokes: BrushStrokeType[]
    activeBrushStrokeIndex: number
    left: number
    top: number
    scaleX: number
    scaleY: number
    cursor: string
}

type RadiusPatternColorType = { r: number, p: string, c: string }
type GroupedLayerType = {
    layer_idx: number
    is_eraser: boolean
    brush_stroke_idxs: number[]
    mask_layer_idx: number
}

const { min, max, abs, sin, cos, PI, round, tan, atan, atan2, ceil, floor, sqrt, pow } = Math

export const RANDOM_ARRAY = Array.from({ length: 250 }, () => randomIntFromInterval(0, 259));

class BrushSvg extends Component<Props, State> {
    rectRef: RefObject<any>

    constructor(props: Props) {
        super(props)
        this.rectRef = React.createRef()

        this.state = {
            ...this.getShapeAndBrushStrokesFromProps(props),
            activeBrushStrokeIndex: 0,
            left: null,
            top: null,
            scaleX: 1,
            scaleY: 1,
            cursor: 'default',
        }
    }

    getShapeAndBrushStrokesFromProps = (props: Props): { brushStrokes: BrushStrokeType[],/*  shape: BrushShape  */ } => {
        return {
            brushStrokes: props.data.brush_strokes ? (typeof props.data.brush_strokes === 'string' && isJson(props.data.brush_strokes) ?
                JSON.parse(props.data.brush_strokes) : Array.isArray(props.data.brush_strokes) ? props.data.brush_strokes : []) : [],
            //shape: props.data,
        }
    }

    componentDidMount(): void {
        this.bindEvents()
        this.updateCursor()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        const brushStrokesDiff = prevProps && JSON.stringify(this.props.data.brush_strokes) !== JSON.stringify(prevProps.data.brush_strokes)

        if (this.props.data.is_draggable && prevProps && (
            this.props.activeShapeId !== prevProps.activeShapeId ||
            this.props.data.angle !== prevProps.data.angle ||
            this.props.data.x !== prevProps.data.x ||
            this.props.data.y !== prevProps.data.y
            //this.props.data.index !== this.state.shape.index
        )
        ) {
            this.bindEvents()
        }

        if (brushStrokesDiff) {
            this.setState({ ...this.getShapeAndBrushStrokesFromProps(this.props) })
        }

        if (prevProps && (
            (prevProps.brushForm.pattern !== this.props.brushForm.pattern) ||
            (prevProps.brushForm.color !== this.props.brushForm.color) ||
            (prevProps.brushForm.radius !== this.props.brushForm.radius) ||
            (prevProps.brushForm.is_eraser !== this.props.brushForm.is_eraser)
        )) {
            this.updateCursor()
        }
    }

    updateCursor = () => {
        const { brushForm, size } = this.props

        const svgImageEl = document.getElementById('image')
        const svgImageBoundingBox = svgImageEl.getBoundingClientRect()

        const scaleX = svgImageBoundingBox.width ? size.width / svgImageBoundingBox.width : 1
        const scaleY = svgImageBoundingBox.height ? size.height / svgImageBoundingBox.height : 1

        this.setState({
            scaleX: scaleX,
            scaleY: scaleY,
        })

        const r = brushForm.radius / scaleX
        const isEraser = brushForm.is_eraser
        const color = isEraser ? '#000000a0' : brushForm.color

        if (brushForm.pattern) {
            getBrushPatternCursor(brushForm.pattern, color, r / 2)
                .then((response: AxiosResponse<any>) => {
                    this.setState({ cursor: `url("${response.data.cursor}") ${r} ${r}, crosshair` })
                })
                .catch((error) => {
                    this.setState({ cursor: 'crosshair' })
                    console.error('cannot get cursor', error)
                })
        } else {
            const svg = `<svg xmlns='http://www.w3.org/2000/svg' xmlns:xlink="http://www.w3.org/1999/xlink" width='${2 * r}px' height='${2 * r}px'>` +
                `<circle cx='${r}' cy='${r}' r='${r - 4}' stroke='#3a3a3a' stroke-width='1' fill='${color}' />` +
                `</svg>`

            this.setState({ cursor: `url("data:image/svg+xml;base64,${btoa(svg)}") ${r} ${r}, crosshair` })
        }
    }

    bindEvents = () => {
        const el = this.rectRef.current
        if (el && d3.select(el)) {
            d3.select(el)
                .call(
                    d3.drag()
                        .filter((event) => event.which === 1)
                        .on("start", this.dragStarted)
                        .on("drag", this.dragged)
                        .on("end", this.dragEnded)
                )
        }
    }

    getBrushSpacingPx = (): number => {
        const { brushForm } = this.props
        return brushForm.spacing * brushForm.radius / 100
    }

    getNewPointPos = (event, scaleX: number, scaleY: number, left: number, top: number): AnyPoint => {
        const { data } = this.props

        const alpha = -(data.angle || 0) / 180 * PI

        const c = cos(alpha)
        const s = sin(alpha)

        const newPointX = (event.sourceEvent.clientX - left) * scaleX - data.x
        const newPointY = (event.sourceEvent.clientY - top) * scaleY - data.y

        return {
            x: round(newPointX * c - newPointY * s),
            y: round(newPointX * s + newPointY * c),
        }
    }

    dragStarted = (event) => {
        const { brushStrokes } = this.state
        const { size, brushForm, isAlt } = this.props

        const formName = getFormName(isAlt)

        const el = this.rectRef.current
        const boundingClientRect = el.getBoundingClientRect()
        const scaleX = boundingClientRect.width ? size.width / boundingClientRect.width : 1
        const scaleY = boundingClientRect.height ? size.height / boundingClientRect.height : 1

        this.setState({
            scaleX: scaleX,
            scaleY: scaleY,
            left: boundingClientRect.left,
            top: boundingClientRect.top,
        })

        const newPoint = this.getNewPointPos(event, scaleX, scaleY, boundingClientRect.left, boundingClientRect.top)

        //add point to line
        const newBrushStroke: BrushStrokeType = {
            points: [newPoint],
            ...brushForm,
        }

        const updatedbrushStrokes = [...brushStrokes, newBrushStroke]

        /* if (brushStrokes.length) {
            const templateSize = getTemplateSize(form)
            const newShape = {
                ...getBrush(templateSize),
                brush_strokes: JSON.stringify(updatedbrushStrokes)
            }

            let newShapes = [];
            newShapes[newShape.index] = newShape
            this.setState({
                shape: newShape,
                brushStrokes: updatedbrushStrokes
            })
            reorderShapes({ ...newShapes, ...form.shapes }, [newShape.index, ...Object.keys(form.shapes)])
            this.props.setActiveShapeId(newShape.index)
        } else { */
        this.setState({
            brushStrokes: updatedbrushStrokes,
            activeBrushStrokeIndex: updatedbrushStrokes.length - 1,

        })
        /* } */
    }

    dragged = (event) => {
        const { brushStrokes, activeBrushStrokeIndex, left, top, scaleX, scaleY } = this.state

        const activeBrushStrokePoints = [...brushStrokes[activeBrushStrokeIndex].points]
        const lastPoint = activeBrushStrokePoints[activeBrushStrokePoints.length - 1]
        const spacing = this.getBrushSpacingPx()  //from percents to pixels

        const newPoint = this.getNewPointPos(event, scaleX, scaleY, left, top)

        let updatedbrushStrokes = [...brushStrokes]

        const distance = lastPoint ? sqrt(pow(newPoint.x - lastPoint.x, 2) + pow(newPoint.y - lastPoint.y, 2)) : null
        if (!spacing || distance > spacing) {
            const subdividedPoints = getSubdividedPoints(activeBrushStrokePoints, spacing)

            updatedbrushStrokes[activeBrushStrokeIndex].points = [...subdividedPoints, newPoint]
            this.setState({ brushStrokes: updatedbrushStrokes })
        }
    }

    dragEnded = (event) => {
        const { activeBrushStrokeIndex, brushStrokes } = this.state
        const { data, isAlt } = this.props

        const formName = getFormName(isAlt)

        let minX = null, minY = null, maxX = null, maxY = null
        const spacing = this.getBrushSpacingPx()

        const updatedBrushStrokes = brushStrokes.map((brushStroke, i) => {
            const brushStrokePoints = (i === activeBrushStrokeIndex) ? getSubdividedPoints(brushStroke.points, spacing) : brushStroke.points

            brushStrokePoints.forEach(point => {
                if (!brushStroke.is_eraser) {
                    if (minX === null || point.x - brushStroke.radius < minX) {
                        minX = point.x - brushStroke.radius
                    }
                    if (minY === null || point.y - brushStroke.radius < minY) {
                        minY = point.y - brushStroke.radius
                    }
                    if (maxX === null || point.x + brushStroke.radius > maxX) {
                        maxX = point.x + brushStroke.radius
                    }
                    if (maxY === null || point.y + brushStroke.radius > maxY) {
                        maxY = point.y + brushStroke.radius
                    }
                }
            })

            return {
                ...brushStroke,
                points: brushStrokePoints,
            }
        })

        const middleX = minX && maxX ? round(.5 * (minX + maxX)) : 0
        const middleY = minY && maxY ? round(.5 * (minY + maxY)) : 0

        const width = maxX - minX
        const height = maxY - minY

        let centeredBrushStrokes = []
        updatedBrushStrokes.forEach(brushStroke => {
            const centeredPoints = brushStroke.points.map(point => {
                return {
                    x: point.x - middleX,
                    y: point.y - middleY,
                }
            })

            centeredBrushStrokes.push({
                ...brushStroke,
                points: centeredPoints,
            })
        })

        const alpha = (data.angle || 0) / 180 * PI

        const c = cos(alpha)
        const s = sin(alpha)

        const dx = round(c * middleX - s * middleY)
        const dy = round(s * middleX + c * middleY)

        this.props.updateInputValue(formName, `shapes.${data.index}`, {
            ...data,
            brush_strokes: JSON.stringify(centeredBrushStrokes),
            width: width,
            height: height,
            x: data.x + dx,
            y: data.y + dy,
        })

        this.setState({ brushStrokes: centeredBrushStrokes })
    }

    getPatternLocalUrl = (shapeIndex: string, radius: number, pattern: string, color: string): string => {
        return `brush-${shapeIndex}-${radius}-${pattern}-${color}`
    }

    render() {
        const { size, activeShapeId, brushForm, data } = this.props
        const { brushStrokes, cursor } = this.state

        const isActive = (activeShapeId === data.index)

        let uniqueUsedPatternsWithRadiusAndColor: RadiusPatternColorType[] = [{ r: brushForm.radius, p: brushForm.pattern, c: brushForm.color }]

        let groupedLayers: GroupedLayerType[] = []
        if (brushStrokes && Array.isArray(brushStrokes) && brushStrokes.length) {
            let thisLayer: GroupedLayerType

            brushStrokes.forEach((brushStroke, i) => {
                const isEraser = brushStroke.is_eraser
                const color = isEraser ? '#000000' : brushStroke.color
                const existing = find(uniqueUsedPatternsWithRadiusAndColor, (u) => {
                    return u.r === brushStroke.radius && u.p === brushStroke.pattern && u.c === color
                })
                if (!existing) {
                    uniqueUsedPatternsWithRadiusAndColor.push({ r: brushStroke.radius, p: brushStroke.pattern, c: color })
                }


                //eraser_brush_stroke_below_idxs: musi obsahovat vsechny vrstvy s eraserem nize:
                let below = []
                if (isEraser) {
                    brushStrokes.forEach((brushStroke, idx) => {
                        if (brushStroke.is_eraser && idx >= i) {
                            below.push(idx)
                        }
                    })
                }

                if (i === 0) {
                    thisLayer = {
                        layer_idx: 0,
                        is_eraser: isEraser,
                        brush_stroke_idxs: isEraser ? below : [i],
                        mask_layer_idx: isEraser ? null : 1
                    }
                } else {
                    if (thisLayer.is_eraser === isEraser) { //same layer
                        thisLayer.brush_stroke_idxs = [...thisLayer.brush_stroke_idxs, i]  //add idx
                    } else {
                        groupedLayers.push({ ...thisLayer })    //push existing
                        thisLayer = {                           //add new layer
                            layer_idx: thisLayer.layer_idx + 1,
                            is_eraser: isEraser,
                            brush_stroke_idxs: isEraser ? below : [i],
                            mask_layer_idx: isEraser ? null : thisLayer.layer_idx + 2 //if exists...
                        }
                    }
                }
            })

            groupedLayers.push({ ...thisLayer })
        }

        const userId = this.props.project.user && this.props.project.user.id ? this.props.project.user.id : null
        const isDebug = testUsers.includes(userId)

        const shapeTransform = `rotate(${-(data.angle || 0)}), translate(${-data.x},${-data.y})`

        return (
            <g className="draggable">
                <defs>
                    {uniqueUsedPatternsWithRadiusAndColor.filter(item => item.p && item.p !== 'null').map((item, i) => {
                        const patternUrlId = this.getPatternLocalUrl(data.index, item.r, item.p, item.c)

                        return <image
                            id={patternUrlId}
                            key={`def-brush-${patternUrlId}`}
                            width={item.r}
                            height={item.r}
                            xlinkHref={item.c ? `${URL.laravelBase}get-brush-pattern/${item.p}/${item.c.substring(1)}` : `${URL.laravelBase}img/brush-strokes/${item.p}`}
                        />
                    })}

                    {groupedLayers.length > 0 &&
                        groupedLayers
                            .filter((groupedLayer: GroupedLayerType) => groupedLayer.is_eraser)
                            .map((groupedLayer: GroupedLayerType) => {
                                const eraserMaskLayerId = `eraser_mask_${data.index}_${groupedLayer.layer_idx}`
                                return <mask key={eraserMaskLayerId} id={eraserMaskLayerId}>
                                    <rect x="0" y="0" width="100%" height="100%" fill="white" transform={shapeTransform}></rect>

                                    {groupedLayer.brush_stroke_idxs.map((idx, j) => {
                                        const brushStroke = brushStrokes[idx]
                                        const isVariableLineWidth = brushStroke.linewidth_profile && brushStroke.linewidth_profile !== LINEWIDTH_PROFILE_UNIFORM
                                        const paths = getPathFromPoints(brushStroke.points, brushStroke.radius, brushStroke.linewidth_profile)
                                        const countPoints = brushStroke.points.length

                                        let absoluteLineLength = 0

                                        return <React.Fragment key={`eraser_${data.index}_${idx}_${j}`}>
                                            {paths &&
                                                <g>
                                                    {!brushStroke.pattern && brushStroke.color &&
                                                        <React.Fragment>
                                                            {!isVariableLineWidth &&
                                                                <path
                                                                    fill={'none'}
                                                                    strokeLinecap='round'
                                                                    strokeLinejoin='round'
                                                                    stroke={'#000000'}
                                                                    strokeWidth={brushStroke.radius}
                                                                    d={paths.base}
                                                                />
                                                            }

                                                            {isVariableLineWidth &&
                                                                <path
                                                                    stroke={'none'}
                                                                    fill={'#000000'}
                                                                    strokeLinecap='round'
                                                                    strokeLinejoin='round'
                                                                    strokeWidth={0}
                                                                    d={paths.around}
                                                                    className='fill-path'
                                                                />
                                                            }
                                                        </React.Fragment>
                                                    }

                                                    {brushStroke.pattern &&
                                                        brushStroke.points.map((point, j) => {
                                                            const isFirst = j === 0
                                                            const isOnePoint = countPoints === 1

                                                            let newAngle = 0
                                                            let scale = 1

                                                            if (!brushStroke.pattern_rotate || brushStroke.linewidth_profile) {    //for pattern rotate or line width profile
                                                                let dx = 0, dy = 0
                                                                if (!isOnePoint) {
                                                                    if (isFirst) {
                                                                        dx = brushStroke.points[1].x - brushStroke.points[0].x
                                                                        dy = brushStroke.points[1].y - brushStroke.points[0].y
                                                                    } else if (typeof brushStroke.points[j + 1] !== 'undefined') {
                                                                        dx = brushStroke.points[j + 1].x - brushStroke.points[j - 1].x
                                                                        dy = brushStroke.points[j + 1].y - brushStroke.points[j - 1].y
                                                                    } else {
                                                                        dx = brushStroke.points[j].x - brushStroke.points[j - 1].x
                                                                        dy = brushStroke.points[j].y - brushStroke.points[j - 1].y
                                                                    }
                                                                }

                                                                //const dx = brushStroke.points[j - 1] ? brushStroke.points[j].x - brushStroke.points[j - 1].x : 0
                                                                //const dy = brushStroke.points[j - 1] ? brushStroke.points[j].y - brushStroke.points[j - 1].y : 0

                                                                absoluteLineLength += sqrt(dx * dx + dy * dy)

                                                                const theta = atan2(dy, dx)
                                                                newAngle = (theta * 180 / PI + 360) % 360

                                                                if (brushStroke.linewidth_profile) {
                                                                    const normalizedPosition = countPoints > 1 ? j / (countPoints - 1) : 0    //something in range [0, ..., 1]
                                                                    const linewidth = getLineWidth(normalizedPosition, absoluteLineLength, brushStroke.radius, brushStroke.linewidth_profile, theta)
                                                                    scale = 2 * linewidth / brushStroke.radius
                                                                }
                                                            }

                                                            if (brushStroke.pattern_rotate) {
                                                                newAngle = RANDOM_ARRAY[j % RANDOM_ARRAY.length]
                                                            }

                                                            const r = .5 * brushStroke.radius * scale

                                                            const patternUrlId = this.getPatternLocalUrl(data.index, brushStroke.radius, brushStroke.pattern, '#000000')

                                                            return <use
                                                                key={`brush_path_${data.index}_point_${idx}_${j}`}
                                                                href={`#${patternUrlId}`}
                                                                transform={`translate(${point.x - r}, ${point.y - r}), rotate(${newAngle}, ${r}, ${r}), scale(${scale})`}
                                                            />
                                                        })
                                                    }
                                                </g>
                                            }
                                        </React.Fragment>
                                    })
                                    }
                                </mask>
                            })
                    }
                </defs>

                <rect
                    id={`brush-${data.index}`}
                    ref={this.rectRef}
                    transform={shapeTransform}
                    width={size.width}
                    height={size.height}
                    fill={'transparent'}
                    onContextMenu={(e) => {
                        this.props.setActiveShapeId(null)
                        e.preventDefault()
                    }}
                    style={{
                        cursor: isActive ? cursor : 'pointer',
                        pointerEvents: isActive ? 'fill' : 'none'
                    }}
                />

                <g className='brush-strokes'>
                    {groupedLayers.length > 0 &&
                        groupedLayers
                            .filter((groupedLayer: GroupedLayerType) => !groupedLayer.is_eraser)
                            .map((groupedLayer: GroupedLayerType, layerIdx: number) => {
                                const eraserMaskLayerId = `eraser_mask_${data.index}_${groupedLayer.mask_layer_idx}`
                                const maskExists = groupedLayer.mask_layer_idx && typeof groupedLayers[groupedLayer.mask_layer_idx] !== 'undefined'

                                return <g mask={maskExists ? `url(#${eraserMaskLayerId})` : undefined}>
                                    {groupedLayer.brush_stroke_idxs.map(idx => {
                                        const brushStroke = brushStrokes[idx]
                                        const countPoints = brushStroke.points.length
                                        const isVariableLineWidth = brushStroke.linewidth_profile && brushStroke.linewidth_profile !== LINEWIDTH_PROFILE_UNIFORM
                                        const paths = getPathFromPoints(brushStroke.points, brushStroke.radius, brushStroke.linewidth_profile)

                                        let absoluteLineLength = 0

                                        return <React.Fragment key={`brush_${data.index}_layer_${idx}`}>
                                            {paths && !brushStroke.pattern && brushStroke.color &&
                                                <React.Fragment>
                                                    {!isVariableLineWidth &&
                                                        <path
                                                            fill={'none'}
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            stroke={brushStroke.color}
                                                            strokeWidth={brushStroke.radius}
                                                            d={paths.base}
                                                        />
                                                    }

                                                    {isVariableLineWidth &&
                                                        <path
                                                            stroke={'none'}
                                                            fill={brushStroke.color}
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            strokeWidth={0}
                                                            d={paths.around}
                                                            /* d={paths.bezier} */
                                                            className='fill-path'
                                                        />
                                                    }

                                                    {/* debug only */}
                                                    {/* {isDebug &&
                                                    <React.Fragment>
                                                        <path
                                                            fill={'none'}
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            stroke={'blue'}
                                                            strokeWidth={1}
                                                            d={paths.base}
                                                        />

                                                        <path
                                                            fill={'none'}
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            stroke={'red'}
                                                            strokeWidth={1}
                                                            d={paths.around}
                                                        />

                                                        <path
                                                            fill={'none'}
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            stroke={'blue'}
                                                            strokeWidth={1}
                                                            d={paths.bezier}
                                                        />

                                                        {paths.controlPoints.map((p, j) => <React.Fragment key={`cp_${j}`}>
                                                            <line x1={p.cx} x2={p.cx2} y1={p.cy} y2={p.cy2} stroke='violet' strokeWidth='1' />
                                                            <circle cx={p.x} cy={p.y} r={2} stroke='black' fill='orange' strokeWidth='1' />

                                                            <rect x={p.cx - 2} y={p.cy - 2} width={4} height={4} stroke='blue' fill='none' strokeWidth='1' />
                                                            <rect x={p.cx2 - 2} y={p.cy2 - 2} width={4} height={4} stroke='green' fill='none' strokeWidth='1' />
                                                        </React.Fragment>)}

                                                        {paths.points.map((p, j) => <React.Fragment key={`cpp_${j}`}>
                                                            <circle cx={p.x} cy={p.y} r={2} stroke='black' fill='orange' strokeWidth='1' />
                                                        </React.Fragment>)}
                                                    </React.Fragment>
                                                } */}
                                                </React.Fragment>
                                            }

                                            {brushStroke.pattern &&
                                                <React.Fragment>
                                                    {brushStroke.points.map((point, j) => {
                                                        const isFirst = j === 0
                                                        const isOnePoint = countPoints === 1

                                                        let newAngle = 0
                                                        let scale = 1

                                                        if (!brushStroke.pattern_rotate || brushStroke.linewidth_profile) {    //for pattern rotate or line width profile
                                                            let dx = 0, dy = 0
                                                                if (!isOnePoint) {
                                                                    if (isFirst) {
                                                                        dx = brushStroke.points[1].x - brushStroke.points[0].x
                                                                        dy = brushStroke.points[1].y - brushStroke.points[0].y
                                                                    } else if (typeof brushStroke.points[j + 1] !== 'undefined') {
                                                                        dx = brushStroke.points[j + 1].x - brushStroke.points[j - 1].x
                                                                        dy = brushStroke.points[j + 1].y - brushStroke.points[j - 1].y
                                                                    } else {
                                                                        dx = brushStroke.points[j].x - brushStroke.points[j - 1].x
                                                                        dy = brushStroke.points[j].y - brushStroke.points[j - 1].y
                                                                    }
                                                                }

                                                            //const dx = brushStroke.points[j - 1] ? brushStroke.points[j].x - brushStroke.points[j - 1].x : 0
                                                            //const dy = brushStroke.points[j - 1] ? brushStroke.points[j].y - brushStroke.points[j - 1].y : 0

                                                            absoluteLineLength += sqrt(dx * dx + dy * dy)

                                                            const theta = atan2(dy, dx)
                                                            newAngle = (theta * 180 / PI + 360) % 360

                                                            if (brushStroke.linewidth_profile) {
                                                                const normalizedPosition = countPoints > 1 ? j / (countPoints - 1) : 0    //something in range [0, ..., 1]
                                                                const linewidth = getLineWidth(normalizedPosition, absoluteLineLength, brushStroke.radius, brushStroke.linewidth_profile, theta)
                                                                scale = 2 * linewidth / brushStroke.radius
                                                            }
                                                        }

                                                        if (brushStroke.pattern_rotate) {
                                                            newAngle = RANDOM_ARRAY[j % RANDOM_ARRAY.length]
                                                        }

                                                        const r = .5 * brushStroke.radius * scale
                                                        const patternUrlId = this.getPatternLocalUrl(data.index, brushStroke.radius, brushStroke.pattern, brushStroke.color)

                                                        return <use
                                                            key={`brush_path_${data.index}_point_${idx}_${j}`}
                                                            href={`#${patternUrlId}`}
                                                            transform={`translate(${point.x - r}, ${point.y - r}), rotate(${newAngle}, ${r}, ${r}), scale(${scale})`}
                                                        />
                                                    })
                                                    }
                                                </React.Fragment>
                                            }
                                        </React.Fragment>
                                    })
                                    }
                                </g>
                            })
                    }
                </g>
            </g>
        )
    }
}

function mapStateToProps(state) {
    return {
        isAlt: state.isAlt,
        form: selectFormValues(state, getFormName(state.isAlt)),
        project: state.project.data,
        activeShapeId: state.activeShapeId,
        brushForm: selectFormValues(state, 'brushForm'),
    }
}

export default connect(mapStateToProps, {
    updateInputValue,
    setActiveShapeId,
})(BrushSvg)